/**
 * App Component
 * Main application with routing
 */

import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore.js';

// Layouts
import { MainLayout, AuthLayout } from '@/components/layout/index.js';
import { ProtectedRoute } from '@/components/ProtectedRoute.js';

// Auth pages
import { LoginPage, RegisterPage, ForgotPasswordPage } from '@/pages/auth/index.js';

// Dashboard
import { DashboardPage } from '@/pages/dashboard/index.js';

// Feature pages
import { CustomersPage } from '@/pages/customers/index.js';
import { ProductsPage } from '@/pages/products/index.js';
import { InvoicesPage } from '@/pages/invoices/index.js';
import { PaymentsPage } from '@/pages/payments/index.js';
import { ExpensesPage } from '@/pages/expenses/index.js';
import { SettingsPage } from '@/pages/settings/index.js';
import { InventoryPage } from '@/pages/inventory/index.js';
import { StaffPage } from '@/pages/staff/index.js';
import { ReportsPage } from '@/pages/reports/index.js';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

// App initialization component
function AppInit({ children }: { children: React.ReactNode }) {
  const { fetchUser, isLoading } = useAuthStore();

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          <p className="mt-4 text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Placeholder component for unimplemented pages
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex h-96 items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <p className="mt-2 text-sm text-gray-500">This page is coming soon.</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppInit>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* Auth routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
            </Route>

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:id" element={<PlaceholderPage title="Customer Details" />} />
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/:id" element={<PlaceholderPage title="Product Details" />} />
              <Route path="invoices" element={<InvoicesPage />} />
              <Route path="invoices/new" element={<PlaceholderPage title="Create Invoice" />} />
              <Route path="invoices/:id" element={<PlaceholderPage title="Invoice Details" />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="payments/:id" element={<PlaceholderPage title="Payment Details" />} />
              <Route path="expenses" element={<ExpensesPage />} />
              <Route path="expenses/:id" element={<PlaceholderPage title="Expense Details" />} />
              <Route path="inventory" element={<InventoryPage />} />
              <Route path="staff" element={<StaffPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="profile" element={<PlaceholderPage title="Profile" />} />
            </Route>

            {/* Catch all */}
            <Route
              path="*"
              element={
                <div className="flex min-h-screen items-center justify-center">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900">404</h1>
                    <p className="mt-2 text-gray-500">Page not found</p>
                    <a
                      href="/dashboard"
                      className="mt-4 inline-block text-primary-600 hover:text-primary-500"
                    >
                      Go to Dashboard
                    </a>
                  </div>
                </div>
              }
            />
          </Routes>
        </AppInit>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
